import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFirestoreDb } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useAsyncSave } from '@/components/settings/useAsyncSave';
import { useOrgSettingsDoc } from '@/components/settings/useOrgSettingsDoc';
import { useSettingsAccess } from '@/components/settings/useSettingsAccess';
import { SettingsFirestoreError } from '@/components/settings/SettingsFirestoreError';
import { MfaEnrollment } from '@/components/MfaEnrollment';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Select } from '@/components/ui/Select';
import { SettingsSection } from '@/components/settings/SettingsSection';

export function SecuritySection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { orgId, online, canEdit } = useSettingsAccess();
  const { raw, loading, error } = useOrgSettingsDoc(orgId, 'security');
  const { saving, run } = useAsyncSave('settings-security');
  const [mfaPolicy, setMfaPolicy] = useState<'optional' | 'required'>('optional');

  useEffect(() => {
    const p = raw?.mfaPolicy;
    if (p === 'required' || p === 'optional') setMfaPolicy(p);
  }, [raw]);

  async function savePolicy() {
    if (!orgId || !canEdit || !online) return;
    await run(async () => {
      await setDoc(
        doc(getFirestoreDb(), 'organizations', orgId, 'settings', 'security'),
        { mfaPolicy, updatedAt: serverTimestamp() },
        { merge: true }
      );
    }, t('settings.securitySaved'));
  }

  if (!user) return null;

  return (
    <SettingsSection id="settings-security" lazy title={t('settings.security')} loading={loading}>
      {(visible) =>
        visible ? (
          <>
            <SettingsFirestoreError message={error ?? undefined} />
            <div>
              <Label htmlFor="mfa-policy">{t('settings.mfaPolicy')}</Label>
              <Select
                className="mt-1 max-w-md"
                value={mfaPolicy}
                disabled={!online || !orgId || !canEdit}
                onValueChange={(v) => setMfaPolicy(v as 'optional' | 'required')}
                options={[
                  { value: 'optional', label: t('settings.mfaOptional') },
                  { value: 'required', label: t('settings.mfaRequired') }
                ]}
              />
            </div>
            {canEdit && (
              <Button
                variant="secondary"
                disabled={!online || !orgId || saving}
                onClick={() => void savePolicy()}
              >
                {t('common.save')}
              </Button>
            )}
            <MfaEnrollment user={user} />
          </>
        ) : null
      }
    </SettingsSection>
  );
}
